import { yupResolver } from '@hookform/resolvers/yup';
import { Box, MenuItem, TextField } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { Button, LoadingButton } from '@apigrowfinance/pbolt-button';
import { PDialogAction, PDialogContent } from '@apigrowfinance/pbolt-dialog';
import dayjs from 'dayjs';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import * as yup from 'yup';

import { ICustomerDetailsFormInputs } from '@clients/accountsClient';
import { CustomerDetails } from '@clients/customersClient';
import useEditUser from '@hooks/queries/useEditUser';
import phoneGenderTypes from '@messages/phoneGenderTypes';
import phoneMaritalStatus from '@messages/phoneMaritalStatus';
import phoneValidations from '@messages/phoneValidations';
import userValidations from '@messages/userValidations';
import getEnumTranslation from '@utils/getEnumTranslation';

interface IFormInputs {
    name: string;
    printedName: string;
    socialName: string;
    email: string;
    nickName: string;
    birthDate: string;
    gender: string;
    mothersName: string;
    maritalStatus: string;
}

const schema = yup.object().shape({
    name: yup.string(),
    printedName: yup.string(),
    socialName: yup.string(),
    email: yup.string(),
    nickName: yup.string(),
    birthDate: yup
        .date()
        .typeError('USEREDITFORMBIRTHDATETYPE')
        .required('USEREDITFORMBIRTHDATE'),
    gender: yup.string().nullable(),
    mothersName: yup.string(),
    maritalStatus: yup.string().nullable(),
});

interface UserEditModalProps {
    customerDetails: CustomerDetails;
    onClickClose: () => void;
    onClickSaved: () => void;
}

const UserEditForm = ({
    customerDetails,
    onClickClose,
    onClickSaved,
}: UserEditModalProps) => {
    const {
        control,
        handleSubmit,
        formState: { isDirty, errors },
    } = useForm<IFormInputs>({
        defaultValues: {
            name: customerDetails?.entity.name || '',
            printedName: customerDetails?.customer.printed_name,
            socialName: customerDetails?.customer.social_name,
            email: customerDetails?.customer.email,
            nickName: customerDetails?.customer.nickname,
            birthDate: dayjs(customerDetails?.entity.birth_date).format(
                'YYYY-MM-DD',
            ),
            gender: customerDetails?.entity.gender || 'N/A',
            mothersName: customerDetails?.entity.mothers_name,
            maritalStatus: customerDetails?.entity.marital_status || 'N/A',
        },
        resolver: yupResolver(schema),
    });
    const { mutate, isLoading } = useEditUser(
        customerDetails.account_id,
        customerDetails.customer.id,
        () => {
            // notify
            onClickSaved();
        },
    );

    const genderOptions = [
        {
            label: 'M',
            value: 'M',
        },
        {
            label: 'F',
            value: 'F',
        },
        {
            label: 'PREFER_NOT_TO_SAY',
            value: 'N/A',
        },
    ];

    const maritalStatusOptions = [
        {
            label: 'SINGLE',
            value: 'SINGLE',
        },
        {
            label: 'MARRIED',
            value: 'MARRIED',
        },
        {
            label: 'DIVORCED',
            value: 'DIVORCED',
        },
        {
            label: 'WIDOWER',
            value: 'WIDOWER',
        },
        {
            label: 'PREFER_NOT_TO_SAY',
            value: 'N/A',
        },
    ];

    const formSubmitHandler: SubmitHandler<IFormInputs> = (
        data: IFormInputs,
    ) => {
        const dataToSave: Partial<ICustomerDetailsFormInputs> = {
            name: data?.name,
            printed_name: data?.printedName,
            social_name: data?.socialName,
            nickname: data?.nickName,
            email: data?.email,
            gender: data?.gender === 'N/A' ? '' : data?.gender,
            mothers_name: data?.mothersName,
            birth_date: dayjs(data.birthDate).format('YYYY-MM-DD'),
            marital_status:
                data?.maritalStatus === 'N/A' ? '' : data?.maritalStatus,
        };
        mutate({ ...dataToSave });
    };

    const handleCloseForm = () => {
        onClickClose();
    };

    return (
        <form
            noValidate
            onSubmit={handleSubmit(formSubmitHandler)}
            style={{ width: '100%' }}>
            <PDialogContent>
                <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={
                                <FormattedMessage
                                    id="CardDetailsForm.name"
                                    defaultMessage="Name"
                                />
                            }
                            sx={{ mb: 2 }}
                            variant="outlined"
                        />
                    )}
                />
                <Controller
                    name="printedName"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={
                                <FormattedMessage
                                    id="CardDetailsForm.printedName"
                                    defaultMessage="Printed name"
                                />
                            }
                            sx={{ mb: 2, mt: 2 }}
                            variant="outlined"
                        />
                    )}
                />
                <Controller
                    name="socialName"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={
                                <FormattedMessage
                                    id="CardDetailsForm.socialName"
                                    defaultMessage="Social name"
                                />
                            }
                            sx={{ mb: 2, mt: 2 }}
                            variant="outlined"
                        />
                    )}
                />
                <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={
                                <FormattedMessage
                                    id="CardDetailsForm.email"
                                    defaultMessage="Email"
                                />
                            }
                            sx={{ mb: 2, mt: 2 }}
                            variant="outlined"
                        />
                    )}
                />
                <Controller
                    name="nickName"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={
                                <FormattedMessage
                                    id="CardDetailsForm.nickname"
                                    defaultMessage="Nickname"
                                />
                            }
                            sx={{ mb: 2, mt: 2 }}
                            variant="outlined"
                        />
                    )}
                />
                <Controller
                    name="birthDate"
                    control={control}
                    render={({ field }) => (
                        <DesktopDatePicker
                            {...field}
                            label={
                                <FormattedMessage
                                    id="CardDetailsForm.birthDate"
                                    defaultMessage="Birth date"
                                />
                            }
                            minDate={dayjs('1920-01-01')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    required
                                    sx={{ mb: 2, mt: 2 }}
                                    error={!!errors.birthDate}
                                    helperText={
                                        errors.birthDate &&
                                        getEnumTranslation(
                                            errors.birthDate?.message,
                                            {
                                                messages: userValidations,
                                            },
                                        )
                                    }
                                />
                            )}
                        />
                    )}
                />
                <Controller
                    name="gender"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <TextField
                            sx={{ mb: 2, mt: 2 }}
                            id="gender"
                            fullWidth
                            select
                            label={
                                <FormattedMessage
                                    id="locationDetailsForm.gender"
                                    defaultMessage="Gender"
                                />
                            }
                            error={!!errors.gender}
                            helperText={
                                errors.gender &&
                                getEnumTranslation(errors.gender?.message, {
                                    messages: phoneValidations,
                                })
                            }
                            onChange={onChange}
                            value={value || ''}>
                            {genderOptions?.map((option) => {
                                return (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}>
                                        <Box>
                                            {getEnumTranslation(option.label, {
                                                messages: phoneGenderTypes,
                                            })}
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </TextField>
                    )}
                />
                <Controller
                    name="mothersName"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={
                                <FormattedMessage
                                    id="CardDetailsForm.mothersMaidenName"
                                    defaultMessage="Mothers maiden name"
                                />
                            }
                            sx={{ mb: 2, mt: 2 }}
                            error={!!errors.mothersName}
                            helperText={
                                errors.mothersName &&
                                getEnumTranslation(
                                    errors.mothersName?.message,
                                    {
                                        messages: userValidations,
                                    },
                                )
                            }
                            variant="outlined"
                        />
                    )}
                />
                <Controller
                    name="maritalStatus"
                    defaultValue=""
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <TextField
                            sx={{ mb: 2, mt: 2 }}
                            id="marital-status"
                            fullWidth
                            select
                            label={
                                <FormattedMessage
                                    id="CardDetailsForm.maritalStatus"
                                    defaultMessage="Marital status"
                                />
                            }
                            error={!!errors.maritalStatus}
                            helperText={
                                errors.maritalStatus &&
                                getEnumTranslation(
                                    errors.maritalStatus?.message,
                                    {
                                        messages: phoneMaritalStatus,
                                    },
                                )
                            }
                            onChange={onChange}
                            value={value || ''}>
                            {maritalStatusOptions?.map((option) => {
                                return (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}>
                                        <Box>
                                            {getEnumTranslation(option.label, {
                                                messages: phoneMaritalStatus,
                                            })}
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </TextField>
                    )}
                />
            </PDialogContent>
            <PDialogAction>
                <Button onClick={handleCloseForm}>
                    {getEnumTranslation('cancel')}
                </Button>
                <LoadingButton
                    type="submit"
                    variant="contained"
                    isLoading={isLoading}
                    disabled={!isDirty}>
                    {getEnumTranslation('submit')}
                </LoadingButton>
            </PDialogAction>
        </form>
    );
};

export default UserEditForm;
